import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Item } from "../models/item_model";
import { createUserOrder, getAddressList, getCartItems, removeAddress, removeCartItem, verifyRazorpayPayment } from "../services/api_service";
import { CartResponse } from "../models/cart_model";
import { useCart } from "../context/cart_context";
import { ToastContainer, toast } from "react-toastify";
import { Address } from "../models/address_model";
import { primaryColor } from "../services/theme_service";
import { onPayWithPhonePe } from "../services/phonepe_service";
import AddAddress from "./add_address";
import { useMediaQuery } from "react-responsive";
import SelectAddress from "./select_address";
import { useRazorpay, RazorpayOrderOptions } from "react-razorpay";

export default function CartPage() {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<Item[]>([]);
  const [cartResponse, setCartResponse] = useState<CartResponse | null>(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const { updateQuantity, populateCart } = useCart();
  const userId = localStorage.getItem("userId");
  const [address, setAddress] = useState<Address | undefined>(undefined);
  const [addAddress, setAddAddress] = useState(false);
  const [selectAddress, setSelectAddress] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [deletingAddress, setDeletingAddress] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
      const { error, isLoading, Razorpay } = useRazorpay();

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const fetchCart = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await getCartItems();
      if (res) {
        setCartResponse(res);
        setCartItems(res.items);
        const total = res.items.reduce(
          (sum, item) => sum + (item.final_price * (item.added_quantity || 1)),
          0
        );
        fetchAddress();
        setTotalAmount(total);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddress = async () => {
    if (!userId || address) return;
    const addressList = await getAddressList(userId);
    const primaryAddress = addressList?.find(addr => addr.is_default);
    setAddress(primaryAddress || undefined);
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const handleQuantityChange = (item: Item, delta: number) => {
    const currentQty = item.added_quantity || 1;
    const newQty = currentQty + delta;

    if (delta === -1 && currentQty === 1) {
      handleRemoveItem(item);
      return;
    }
    if (newQty < 1) return;
    if (newQty > item.quantity) {
      toast(`Maximum quantity available is ${item.quantity}`);
      return;
    }

    updateQuantity(item.item_id, delta);
    fetchCart();
  };

  const handleRemoveItem = async (item: Item) => {
    if (userId) {
      await removeCartItem(userId, item.cart_id);
      fetchCart();
      populateCart(userId);
    }
  };

const payWithRzPay = (
  userId: string | number,
  cartItems: Item[],
  address: Address
): Promise<number | null> => {
  return new Promise(async (resolve) => {
    try {
      const orderData = cartItems.map(item => ({
        item_id: item.item_id,
        item_count: item.added_quantity || 1,
      }));

      const orderRes = await createUserOrder(userId, {
        items: orderData,
        address_id: address.address_id!,
        is_from_web: true,
        payment_gateway: "razorpay",
      });

      if (!orderRes) {
        toast.warning("Order creation failed");
        return resolve(null);
      }

      const options: RazorpayOrderOptions = {
        key: "rzp_test_RxRalHX819fIOv",
        amount: orderRes.total_price * 100,
        currency: "INR",
        name: "Manthris",
        description: `Order ${orderRes.order_number}`,
        order_id: orderRes.razor_pay_id,

        handler: async (response) => {
          try {
          await verifyRazorpayPayment(orderRes.order_id, {
              razor_pay_order_id: response.razorpay_order_id,
              razor_pay_payment_id: response.razorpay_payment_id,
              razor_pay_signature: response.razorpay_signature,
            });

            toast.success("Payment Successful ✅");
            resolve(orderRes.order_id); // ✅ RETURN ORDER ID
          } catch {
            toast.warning("Payment verification failed ❌");
            resolve(null);
          }
        },

        prefill: {
          name: "Manthris",
          email: "john.doe@example.com",
          contact: "9999999999",
        },

        theme: { color: primaryColor },

        modal: {
          ondismiss: () => {
            toast.warning("Payment cancelled ❌");
            resolve(null);
          },
        },
      };

      const rzp = new Razorpay(options);

      rzp.on("payment.failed", async (response) => {
        toast.warning("Payment Failed ❌");

        await verifyRazorpayPayment(orderRes.order_id, {
          razor_pay_order_id: orderRes.razor_pay_id,
          error_code: response.error.code,
          error_description: response.error.description,
          error_reason: response.error.reason,
        });

        resolve(null);
      });

      rzp.open();
    } catch (err) {
      toast.warning("Payment Failed ❌");
      resolve(null);
    }
  });
};


  const handleApplyCoupon = () => {
    setCouponLoading(true);
    setCouponError("");
    setTimeout(() => {
      setCouponLoading(false);
      setCouponError("Coupon code not applicable");
    }, 1000);
  };

  const proceedToCheckout = async () => {
    if (!address) {
      toast("Please add address");
      return;
    }

    if (userId) {
      setProcessingPayment(true);
      try {
        const orderId = await payWithRzPay( userId, cartItems, address);
        if(orderId) {
                  navigate(`/orderStatus?id=${orderId}`);
        }
        // if (order?.success && order.message) {
        //   window.location.href = order.message;
        // } else {
        //   toast(order?.message || "Failed to initiate payment");
        // }
      } catch (err) {
        console.error(err);
        toast("Error processing payment");
      } finally {
        setProcessingPayment(false);
      }
    }
  };


  const handleDeleteAddress = async (id: any) => {
    if (!userId || deletingAddress) return;
    setDeletingAddress(true);
    const success = await removeAddress(userId, id);
    setDeletingAddress(false);
    if (success) fetchAddress();
    else toast.error("Failed to delete primary address");
  };

  // ---------------- Web View ----------------
  const renderWebView = () => {
    return (
      <div style={webStyles.page}>
        {/* Header */}
        <div style={webStyles.headerContainer}>
          <div style={webStyles.header}>
            <span style={webStyles.headerText}>
              Home / Collections / Your Shopping Cart
            </span>
          </div>
        </div>

        {processingPayment && (
          <div style={overlayStyles.container}>
            <div style={overlayStyles.content}>
              <div style={overlayStyles.spinner}></div>
              <p style={overlayStyles.text}>Processing Payment...</p>
            </div>
          </div>
        )}
        {/* Loading Overlay */}
        {loading && (
          <div style={webStyles.overlay}>
            <div style={webStyles.spinner}></div>
          </div>
        )}

        {/* Main Content */}
        <div style={webStyles.mainRow}>
          {/* Left Column: Cart Items */}
          <div style={webStyles.cartColumn}>
            {/* Address Section */}
            <div style={webStyles.addressSection}>
              <div style={webStyles.addressHeader}>
                <span style={webStyles.addressTitle}>Shipping Address</span>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    style={webStyles.addAddressBtn}
                    onClick={() => setAddAddress(true)}
                  >
                    + Add New Address
                  </button>
                  <button
                    style={webStyles.selectAddressBtn}
                    onClick={() => setSelectAddress(true)}
                  >
                    Select Address
                  </button>
                </div>
              </div>


              <div style={webStyles.addressList}>
                {address ? (
                  <div style={webStyles.addressItem}>
                    <div style={webStyles.addressLeft}>
                      <div style={webStyles.addressText}>
                        <p style={webStyles.addressName}>
                          {address.first_name} {address.last_name}
                        </p>
                        <p style={webStyles.addressLine}>
                          {address.address1}, {address.address2 && `${address.address2}, `}
                          {address.land_mark && `${address.land_mark}, `}
                          {address.city}, {address.state} - {address.zip_code}, {address.country}
                        </p>
                        <p style={webStyles.addressContact}>
                          📞 {address.phone_number}
                          {address.email && (
                            <>
                              <br /> 📧 {address.email}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <div style={webStyles.actionsContainer}>
                      <div onClick={() => handleDeleteAddress(address.address_id)} style={webStyles.deleteButton}>🗑</div>
                      <button onClick={() => setEditAddress(true)} style={webStyles.editAddressBtn}>Edit</button>
                    </div>
                  </div>
                ) : (
                  <p>No address available</p>
                )}
              </div>
            </div>

            {/* Cart Items */}
            <div style={webStyles.cartBox}>
              {cartItems.length === 0 ? (
                <p style={{ padding: 20 }}>Your cart is empty</p>
              ) : (
                <>
                  {cartItems.map((item, index) => (
                    <React.Fragment key={item.item_id}>
                      <div style={webStyles.cartItemRow}>
                        <img
                          src={item.item_images[0]?.document_url}
                          alt={item.title}
                          style={webStyles.cartItemImage}
                        />
                        <div style={webStyles.cartItemDetails}>
                          <p style={webStyles.cartItemName}>{item.title}</p>
                          <div style={webStyles.cartItemActions}>
                            <button
                              style={webStyles.deleteBtn}
                              onClick={() => handleRemoveItem(item)}
                            >
                              🗑 Delete
                            </button>
                            <div style={webStyles.quantityBox}>
                              <button
                                style={webStyles.qtyButton}
                                onClick={() => handleQuantityChange(item, -1)}
                              >
                                -
                              </button>
                              <span style={webStyles.qtyValue}>{item.added_quantity || 1}</span>
                              <button
                                style={webStyles.qtyButton}
                                onClick={() => handleQuantityChange(item, 1)}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                        <span style={webStyles.cartItemPrice}>
                          ₹{(item.final_price * (item.added_quantity || 1)).toFixed(2)}
                        </span>
                      </div>
                      {index < cartItems.length - 1 && <div style={webStyles.divider} />}
                    </React.Fragment>
                  ))}
                  <div style={webStyles.divider}></div>
                  <div style={webStyles.subtotalRow}>
                    <span>Subtotal</span>
                    <span>₹{totalAmount.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column: Order Summary */}
          <div style={webStyles.summaryColumn}>
            <div style={webStyles.summaryBox}>
              <h3>Order Summary</h3>

              {/* Apply Coupon */}
              {/* <div style={webStyles.couponContainer}>
                <div style={webStyles.couponBoxWithTitle}>
                  <div style={webStyles.couponTitle}>Apply Coupon Code</div>
                  <div style={webStyles.couponInputRow}>
                    <input
                      type="text"
                      placeholder="Coupon Code"
                      style={webStyles.couponInput}
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                      disabled={couponLoading}
                    />
                    <button
                      style={webStyles.applyBtn}
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                    >
                      {couponLoading ? "Applying..." : "Apply"}
                    </button>
                  </div>
                  {couponError && <div style={webStyles.couponError}>{couponError}</div>}
                </div>
              </div> */}

              {/* Price Details */}
              <div style={webStyles.priceDetails}>
                <div style={webStyles.priceRow}>
                  <span>Subtotal</span>
                  <span>₹{cartResponse?.summary?.cart_total}</span>
                </div>
                <div style={webStyles.priceRow}>
                  <span>Discount</span>
                  <span>- ₹{cartResponse?.summary?.discount}</span>
                </div>
                <div style={webStyles.priceRow}>
                  <span>GST</span>
                  <span>₹{cartResponse?.summary?.gst}</span>
                </div>
                <div style={webStyles.priceRow}>
                  <span>Shipping Charges</span>
                  <span>₹{cartResponse?.summary?.shipping_charges}</span>
                </div>

                <div style={webStyles.grandTotalRow}>
                  <span>Grand Total</span>
                  <span>₹{cartResponse?.summary?.total_amount}</span>
                </div>
              </div>

              {/* Proceed & Continue */}
              <button style={webStyles.proceedBtn} onClick={proceedToCheckout}>
                Proceed to Checkout
              </button>
              <button style={webStyles.continueBtn} onClick={() => navigate("/")}>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
        {(addAddress || editAddress) && (
          <AddAddress
            open={addAddress || editAddress}
            address={editAddress ? address : undefined}
            onSave={fetchCart}
            onClose={() => { setAddAddress(false); setEditAddress(false) }}
          />
        )}
        {selectAddress && (
          <SelectAddress
            open={selectAddress}
            selectedAddress={address}
            onSelect={(addr) => { setAddress(addr); setSelectAddress(false) }}
            onClose={() => { setSelectAddress(false) }}
          />
        )}
      </div>

    );
  };

  // ---------------- Mobile View ----------------
  const renderMobileView = () => {
    return (
      <div style={mobileStyles.page}>
        <div style={mobileStyles.header}>
          <span style={mobileStyles.headerText}>Home / Collections / Your Shopping Cart</span>
        </div>

        {processingPayment && (
          <div style={overlayStyles.container}>
            <div style={overlayStyles.content}>
              <div style={overlayStyles.spinner}></div>
              <p style={overlayStyles.text}>Processing Payment...</p>
            </div>
          </div>
        )}
        {loading && (
          <div style={mobileStyles.overlay}>
            <div style={mobileStyles.spinner}></div>
          </div>
        )}

        <div style={mobileStyles.mainColumn}>
          {/* Address Section */}
          <div style={mobileStyles.addressSection}>
            <div style={mobileStyles.addressHeader}>
              <span style={mobileStyles.addressTitle}>Shipping Address</span>
            </div>

            <div style={mobileStyles.addressList}>
              {address ? (
                <div style={mobileStyles.addressItem}>
                  <div style={mobileStyles.addressLeft}>
                    <div style={mobileStyles.addressText}>
                      <p style={mobileStyles.addressName}>{address.first_name} {address.last_name}</p>
                      <p style={mobileStyles.addressLine}>
                        {address.address1}, {address.address2 && `${address.address2}, `}
                        {address.land_mark && `${address.land_mark}, `}
                        {address.city}, {address.state} - {address.zip_code}, {address.country}
                      </p>
                      <p style={mobileStyles.addressContact}>
                        📞 {address.phone_number}
                        {address.email && <><br /> 📧 {address.email}</>}
                      </p>
                    </div>
                  </div>
                  <div style={mobileStyles.actionsContainer}>
                    <div style={mobileStyles.deleteButton} onClick={() => handleDeleteAddress(address.address_id)}>🗑</div>
                    <button style={mobileStyles.editAddressBtn} onClick={() => setEditAddress(true)}>Edit</button>
                  </div>
                </div>
              ) : (
                <p>No address available</p>
              )}
            </div>

            {/* Row with Add & Select Buttons */}
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              <button
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  border: `2px solid ${primaryColor}`,
                  backgroundColor: "transparent",
                  color: primaryColor,
                  cursor: "pointer",
                }}
                onClick={() => setAddAddress(true)}
              >
                Add New Address
              </button>
              <button
                style={{
                  flex: 1,
                  padding: 10,
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: primaryColor,
                  color: "#fff",
                  cursor: "pointer",
                }}
                onClick={() => setSelectAddress(true)}
              >
                Select Address
              </button>
            </div>
            {(addAddress || editAddress) && (
              <AddAddress
                open={addAddress || editAddress}
                address={editAddress ? address : undefined}
                onSave={fetchCart}
                onClose={() => { setAddAddress(false); setEditAddress(false) }}
              />
            )}
            {selectAddress && (
              <SelectAddress
                open={selectAddress}
                selectedAddress={address}
                onSelect={(addr) => { setAddress(addr); setSelectAddress(false) }}
                onClose={() => { setSelectAddress(false) }}
              />
            )}
          </div>

          {/* Cart Items */}
          <div style={mobileStyles.cartBox}>
            {cartItems.length === 0 ? (
              <p style={{ padding: 20 }}>Your cart is empty</p>
            ) : (
              cartItems.map((item, idx) => (
                <div key={item.item_id} style={mobileStyles.cartItemRow}>
                  <img src={item.item_images[0]?.document_url} alt={item.title} style={mobileStyles.cartItemImage} />
                  <div style={mobileStyles.cartItemDetails}>
                    <p style={mobileStyles.cartItemName}>{item.title}</p>
                    <div style={mobileStyles.cartItemActions}>
                      <button style={mobileStyles.deleteBtn} onClick={() => handleRemoveItem(item)}>🗑 Delete</button>
                      <div style={mobileStyles.quantityBox}>
                        <button style={mobileStyles.qtyButton} onClick={() => handleQuantityChange(item, -1)}>-</button>
                        <span style={mobileStyles.qtyValue}>{item.added_quantity || 1}</span>
                        <button style={mobileStyles.qtyButton} onClick={() => handleQuantityChange(item, 1)}>+</button>
                      </div>
                    </div>
                  </div>
                  <span style={mobileStyles.cartItemPrice}>₹{(item.final_price * (item.added_quantity || 1)).toFixed(2)}</span>
                  {idx < cartItems.length - 1 && <div style={mobileStyles.divider}></div>}
                </div>
              ))
            )}
            <div style={mobileStyles.subtotalRow}>
              <span>Subtotal</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
          </div>

          {/* Order Summary */}
          <div style={mobileStyles.summaryBox}>
            <h3 style={{ marginTop: 5, marginBottom: 5 }}>Order Summary</h3>

            {/* <div style={mobileStyles.couponContainer}>
              <div style={mobileStyles.couponBoxWithTitle}>
                <div style={mobileStyles.couponTitle}>Apply Coupon Code</div>
                <div style={mobileStyles.couponInputRow}>
                  <input
                    type="text"
                    placeholder="Coupon Code"
                    style={mobileStyles.couponInput}
                    value={coupon}
                    onChange={e => setCoupon(e.target.value)}
                    disabled={couponLoading}
                  />
                  <button style={mobileStyles.applyBtn} onClick={handleApplyCoupon} disabled={couponLoading}>
                    {couponLoading ? "Applying..." : "Apply"}
                  </button>
                </div>
                {couponError && <div style={mobileStyles.couponError}>{couponError}</div>}
              </div>
            </div> */}

            {/* Price details */}
            <div style={mobileStyles.priceDetails}>
              <div style={mobileStyles.priceRow}><span>Subtotal</span><span>₹{cartResponse?.summary?.cart_total}</span></div>
              <div style={mobileStyles.priceRow}><span>Discount</span><span>- ₹{cartResponse?.summary?.discount}</span></div>
              <div style={mobileStyles.priceRow}><span>GST</span><span>₹{cartResponse?.summary?.gst}</span></div>
              <div style={mobileStyles.priceRow}><span>Shipping Charges</span><span>₹{cartResponse?.summary?.shipping_charges}</span></div>
              <div style={mobileStyles.grandTotalRow}><span>Grand Total</span><span>₹{cartResponse?.summary?.total_amount}</span></div>
            </div>

            <button style={mobileStyles.proceedBtn} onClick={proceedToCheckout}>Proceed to Checkout</button>
            <button style={mobileStyles.continueBtn} onClick={() => navigate("/")}>Continue Shopping</button>
          </div>
        </div>
      </div>
    );
  };

  return isMobile ? renderMobileView() : renderWebView();
}

const overlayStyles = {
  container: {
    position: "fixed" as "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  content: {
    backgroundColor: "#fff",
    padding: 20,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
  },
  spinner: {
    border: "4px solid #f3f3f3",
    borderTop: `4px solid ${primaryColor}`,
    borderRadius: "50%",
    width: 40,
    height: 40,
    animation: "spin 1s linear infinite",
    marginBottom: 10,
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: "#333",
  },
};



// ----------------- Web Styles -----------------
const webStyles: { [key: string]: React.CSSProperties } = {
  page: { background: "#fff", fontFamily: "Arimo, sans-serif", padding: 0, margin: 0 },
  headerContainer: { backgroundColor: "#fff", overflow: "hidden" },
  header: { backgroundColor: "#693B2F", padding: "19px 0" },
  headerText: { color: "#fff", fontSize: 16, fontWeight: 500, margin: "0 5%" },

  mainRow: { display: "flex", gap: "20px", margin: "20px 5%" },
  cartColumn: { flex: 2, display: "flex", flexDirection: "column", gap: "20px" },
  cartBox: { backgroundColor: "#fff", borderRadius: "10px", border: "1px solid #eee", padding: "20px", display: "flex", flexDirection: "column", gap: "16px" },
  cartItemRow: { display: "flex", alignItems: "center", gap: "16px" },
  cartItemImage: { width: 80, height: 80, objectFit: "cover", borderRadius: "8px" },
  cartItemDetails: { flex: 1, display: "flex", flexDirection: "column", gap: "6px" },
  cartItemName: { fontWeight: 600 },
  cartItemActions: { display: "flex", alignItems: "center", gap: "10px" },
  deleteBtn: { background: "transparent", border: "none", color: "#e91e63", cursor: "pointer" },
  quantityBox: { display: "flex", alignItems: "center", border: "1px solid #ddd", borderRadius: "8px", padding: "2px 6px", gap: "6px" },
  qtyButton: { border: "none", background: "transparent", cursor: "pointer", fontWeight: 700, color: "#e91e63", fontSize: "16px" },
  qtyValue: { minWidth: 20, textAlign: "center" },
  cartItemPrice: { fontWeight: 700 },
  divider: { borderBottom: "1px solid #eee", margin: "8px 0" },
  subtotalRow: { display: "flex", justifyContent: "flex-end", fontWeight: 700, gap: 100, marginBottom: 10 },

  summaryColumn: { flex: 1, display: "flex", flexDirection: "column", gap: "20px" },
  summaryBox: { backgroundColor: "#F8F1F1", borderRadius: "10px", paddingLeft: "20px", paddingRight: "20px", paddingBottom: "25px", display: "flex", flexDirection: "column" },

  couponContainer: { display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" },
  couponBoxWithTitle: { backgroundColor: "#fff", padding: "12px", borderRadius: "8px", display: "flex", flexDirection: "column", gap: "8px", border: "1px solid #ddd" },
  couponTitle: { fontWeight: 600, fontSize: "15px", marginBottom: 7, marginTop: 5 },
  couponInputRow: { display: "flex", gap: "8px" },
  couponInput: { flex: 1, padding: "15px", border: "1px solid #ddd", borderRadius: "6px", fontSize: 15 },
  applyBtn: { backgroundColor: primaryColor, color: "#fff", border: "none", padding: "8px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: 500, fontSize: "16px" },
  couponError: { color: "#e91e63", fontSize: "13px", marginTop: "4px" },

  priceDetails: { backgroundColor: "#fff", borderRadius: "8px", padding: "12px", display: "flex", flexDirection: "column", gap: "15px" },
  priceRow: { display: "flex", justifyContent: "space-between", fontSize: "15px", color: "#555" },
  grandTotalRow: { display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "16px", borderTop: "1px solid #ddd", paddingTop: "12px", paddingBottom: "5px" },
  proceedBtn: { marginTop: "20px", padding: "12px", borderRadius: "8px", backgroundColor: primaryColor, color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "17px", marginBottom: "16px" },
  continueBtn: { padding: "12px", borderRadius: "8px", border: "1px solid #e91e63", backgroundColor: "#fff", color: "#e91e63", cursor: "pointer", fontWeight: 700, fontSize: "17px" },

  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" },
  loadingBox: { backgroundColor: "#F8F1F1", padding: "40px", borderRadius: "12px", textAlign: "center", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", width: "70%" },
  loadingText: { fontSize: "18px", fontWeight: 600, color: "#693B2F" },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.2)", // semi-transparent
    backdropFilter: "blur(4px)", // blur effect
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "6px solid #f3f3f3", // light gray
    borderTop: "6px solid #e91e63", // pink
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  addressSection: {
    backgroundColor: "#F8F1F1",
    borderRadius: "10px",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  addressHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  addressTitle: {
    fontSize: "16px",
    fontWeight: 600,
  },
  addAddressBtn: {
    backgroundColor: "transparent",      // transparent for add
    color: primaryColor,                 // text color primary
    border: `2px solid ${primaryColor}`, // border primary
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: 15,
  },
  selectAddressBtn: {
    backgroundColor: primaryColor, // primary fill
    color: "#fff",                 // white text
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: 15,
  },

  addressList: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  addressItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.2s ease-in-out",
  },
  addressItemHover: {
    boxShadow: "0 6px 14px rgba(0,0,0,0.1)",
    transform: "translateY(-2px)",
  },
  addressLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  addressText: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    color: "#333",
  },
  addressName: {
    fontSize: "16px",
    fontWeight: 600,
    margin: 0,
  },
  addressLine: {
    fontSize: "15px",
    color: "#555",
    margin: 0,
    lineHeight: "1.4",
  },
  addressContact: {
    fontSize: "15px",
    color: "#666",
    marginBottom: 2,
    lineHeight: "1.6",
  },
  editAddressBtn: {
    backgroundColor: primaryColor,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "8px 14px",
    fontSize: "14px",
    cursor: "pointer",
    transition: "background 0.3s",
  },
  editAddressBtnHover: {
    backgroundColor: "#e73370",
  },
  actionsContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  deleteButton: {
    backgroundColor: "#f5f5f5",
    padding: "6px 12px",
    borderRadius: "8px",
    fontSize: "16px",
    cursor: "pointer",
    color: "#333",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

};


// ----------------- Mobile Styles -----------------
const mobileStyles: { [key: string]: React.CSSProperties } = {
  page: { background: "#fff", fontFamily: "Arimo, sans-serif", padding: "10px" },
  header: { backgroundColor: "#693B2F", padding: "20px", borderRadius: "8px", marginBottom: "10px" },
  headerText: { color: "#fff", fontSize: 14, fontWeight: 500 },

  mainColumn: { display: "flex", flexDirection: "column", gap: "15px" },
  cartBox: { backgroundColor: "#fff", borderRadius: "8px", padding: "15px", display: "flex", flexDirection: "column", gap: "12px" },
  cartItemRow: { display: "flex", alignItems: "center", gap: "12px" },
  cartItemImage: { width: 60, height: 60, objectFit: "cover", borderRadius: "6px" },
  cartItemDetails: { flex: 1, display: "flex", flexDirection: "column", gap: "4px" },
  cartItemName: { fontWeight: 600, fontSize: 14 },
  cartItemActions: { display: "flex", alignItems: "center", gap: "8px" },
  deleteBtn: { background: "transparent", border: "none", color: "#e91e63", cursor: "pointer", fontSize: 12 },
  quantityBox: { display: "flex", alignItems: "center", border: "1px solid #ddd", borderRadius: "6px", padding: "2px 4px", gap: "4px" },
  qtyButton: { border: "none", background: "transparent", cursor: "pointer", fontWeight: 700, color: "#e91e63", fontSize: 14 },
  qtyValue: { minWidth: 16, textAlign: "center", fontSize: 13 },
  cartItemPrice: { fontWeight: 700, fontSize: 14 },
  divider: { borderBottom: "1px solid #eee", margin: "6px 0" },
  subtotalRow: { display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14, marginBottom: 8 },

  summaryBox: { backgroundColor: "#F8F1F1", borderRadius: "8px", padding: "12px", display: "flex", flexDirection: "column", gap: "12px" },
  couponContainer: { display: "flex", flexDirection: "column", gap: "6px", marginBottom: "12px" },
  couponBoxWithTitle: { backgroundColor: "#fff", padding: "10px", borderRadius: "6px", display: "flex", flexDirection: "column", gap: "6px", border: "1px solid #ddd" },
  couponTitle: { fontWeight: 600, fontSize: "14px", marginBottom: 4, marginTop: 4 },
  couponInputRow: { display: "flex", gap: "6px" },
  couponInput: { flex: 1, padding: "10px", border: "1px solid #ddd", borderRadius: "6px", fontSize: 13 },
  applyBtn: { backgroundColor: primaryColor, color: "#fff", border: "none", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontWeight: 500, fontSize: 14 },
  couponError: { color: "#e91e63", fontSize: "12px", marginTop: "2px" },

  priceDetails: { backgroundColor: "#fff", borderRadius: "6px", padding: "10px", display: "flex", flexDirection: "column", gap: "10px" },
  priceRow: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#555" },
  grandTotalRow: { display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14, borderTop: "1px solid #ddd", paddingTop: "8px", paddingBottom: "4px" },
  proceedBtn: { marginTop: "12px", padding: "10px", borderRadius: "6px", backgroundColor: primaryColor, color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 15, marginBottom: "5px" },
  continueBtn: { padding: "10px", borderRadius: "6px", border: "1px solid #e91e63", backgroundColor: "#fff", color: "#e91e63", cursor: "pointer", fontWeight: 700, fontSize: 15 },

  overlay: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 },
  spinner: { width: "40px", height: "40px", border: "5px solid #f3f3f3", borderTop: "5px solid #e91e63", borderRadius: "50%", animation: "spin 1s linear infinite" },

  addressSection: { backgroundColor: "#F8F1F1", borderRadius: "8px", padding: "12px", display: "flex", flexDirection: "column", gap: "10px" },
  addressHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  addressTitle: { fontSize: 14, fontWeight: 600 },
  addAddressBtn: { backgroundColor: primaryColor, color: "#fff", border: "none", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontWeight: 500, fontSize: 13 },
  addressList: { backgroundColor: "#fff", borderRadius: "6px", padding: "12px", display: "flex", flexDirection: "column", gap: "10px" },
  addressItem: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  addressLeft: { display: "flex", alignItems: "center", gap: "8px", flex: 1 },
  addressText: { display: "flex", flexDirection: "column", gap: "2px", color: "#333" },
  addressName: { fontSize: 14, fontWeight: 600, margin: 0 },
  addressLine: { fontSize: 13, color: "#555", margin: 0, lineHeight: 1.4 },
  addressContact: { fontSize: 13, color: "#666", marginBottom: 2, lineHeight: 1.4 },
  editAddressBtn: { backgroundColor: primaryColor, color: "#fff", border: "none", borderRadius: "6px", padding: "6px 10px", fontSize: 12, cursor: "pointer" },
  actionsContainer: { display: "flex", gap: "6px", alignItems: "center" },
  deleteButton: { backgroundColor: "#f5f5f5", padding: "4px 8px", borderRadius: "6px", fontSize: 14, cursor: "pointer", color: "#333", display: "flex", alignItems: "center", justifyContent: "center" },
};
